import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/csr/DotsThreeVertical';
import { useTranslation } from 'react-i18next';

import styles from './DataTable.module.css';

/**
 * The per-row overflow menu.
 *
 * Handoff, Data tables / Actions: "one overflow menu (ph-dots-three-vertical)
 * per row, revealed on hover/focus, always keyboard reachable. The 'Update |
 * Delete' text links are removed." And under Interactions: "menu is a
 * --shadow-md popover, radius md."
 *
 * "Always keyboard reachable" is the load-bearing half, and it is what the
 * plan's `<button className={styles.actions}>⋮</button>` — a trigger with
 * nothing behind it — cannot deliver. The reveal is `opacity`, never `display`
 * or `visibility`: both of those take the trigger out of the tab order, which
 * turns "revealed on hover" into "unreachable without a mouse". See
 * DataTable.module.css.
 *
 * Keyboard contract (WAI-ARIA menu button): ArrowDown opens and lands on the
 * first item, Arrow keys walk and wrap, Home/End jump, Escape closes and
 * returns focus to the trigger, Tab closes and leaves. Every handler sits on a
 * real <button>, so nothing here needs a key listener on a non-focusable node —
 * which is also what keeps `jsx-a11y` quiet without a single suppression.
 *
 * Closing on blur uses `relatedTarget` on the wrapper rather than a document
 * listener: one subscription per open menu, no cleanup to leak, and it cannot
 * fight React 18's root-level delegation the way a native document listener can
 * (task 21 §6.2).
 */

export interface RowAction<R> {
  label: string;
  onSelect: (row: R) => void;
}

interface RowActionsMenuProps<R> {
  actions: readonly RowAction<R>[];
  row: R;
  /** Names the trigger after the record it acts on. */
  rowLabel: string | null;
}

export function RowActionsMenu<R>({ actions, row, rowLabel }: RowActionsMenuProps<R>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function moveTo(index: number) {
    const wrapped = (index + actions.length) % actions.length;
    itemRefs.current[wrapped]?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    // Otherwise ArrowDown scrolls the page out from under the row.
    event.preventDefault();
    setOpen(true);
  }

  function onItemKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveTo(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveTo(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveTo(0);
        break;
      case 'End':
        event.preventDefault();
        moveTo(actions.length - 1);
        break;
      case 'Escape':
        // Stopped so an Escape aimed at this menu cannot also close the dialog
        // or panel the table is sitting in — task 21's Combobox rule.
        event.stopPropagation();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
    }
  }

  return (
    <div
      className={styles['actionsWrap']}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles['actionsBtn']}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          rowLabel === null ? t('table.rowActions') : t('table.rowActionsFor', { row: rowLabel })
        }
        onClick={() => setOpen((was) => !was)}
        onKeyDown={onTriggerKeyDown}
      >
        <DotsThreeVerticalIcon size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className={styles['menu']} role="menu" aria-label={t('table.rowActions')}>
          {actions.map((action, index) => (
            <button
              key={action.label}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles['menuItem']}
              onKeyDown={(event) => onItemKeyDown(event, index)}
              onClick={() => {
                action.onSelect(row);
                close(true);
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
